import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { ContractError } from 'src/app/core/models/models';
import { OptionType } from 'src/app/core/enums/enums';
import { ItineraryService } from 'src/app/search-result/services/itinerary.service';
import { take } from 'rxjs/operators';
import { IAdultSelected } from './infant-reasignation-modal/infant-reasignation-modal.component';
import { InfantControlService } from 'src/app/search-result/services/infant-control.service';
import { Observable, Subscription } from 'rxjs';
import { SettingsService } from 'src/app/settings/services/settings.service';
import {
  IAdditionalServicesPax,
  Passenger,
  PassengerQuote
} from 'src/app/core/models/itinerary.model';

@Component({
  selector: 'app-passengers',
  templateUrl: './passengers.component.html',
  styleUrls: ['./passengers.component.scss']
})
export class PassengersComponent implements OnInit, OnDestroy {
  @Input() passengers: Passenger[];
  @Input() enabledCheckboxes = true;
  @Input() showTitle = true;
  @Input() somePassengerHasTicket = false;
  @Input() set restart(value: ContractError) {
    if (value) {
      this.reset();
    }
  }
  @Input() set mode(value: OptionType) {
    if (value === OptionType.InvoluntaryChange && this.somePassengerHasTicket) {
      this.enabledCheckboxes = false;
      this.selectAll(true);
    }
    this.isAirline$?.subscribe((x) => {
      this.isAirline = x;
    });
    if (value === OptionType.OpenTicket) {
      this.enabledCheckboxes = false;
    }
    if (this.ticketsReissuedByAirline.length > 0 && this.isAirline) {
      this.somePassengerHasTicket = false;
      this.enabledCheckboxes =
        value === OptionType.OpenTicket ||
        value === OptionType.InvoluntaryChange ||
        value === OptionType.ExchangeNoCost
          ? true
          : false;
      this.somePassengerHasTicket = true;
    } else {
      this.enabledCheckboxes =
        value === OptionType.InvoluntaryChange || !this.somePassengerHasTicket ? false : true;
    }
  }

  @Output() selectPassenger = new EventEmitter<Passenger[]>();

  subscriptions = new Subscription();

  isAirline$: Observable<boolean>;

  isAirline: boolean;
  passengersCheck: Passenger[] = [];
  allPassengersCheck = false;
  showSpecialRequests = false;
  showAncillaries = false;
  showInfantReasignationModal = false;
  isInfantReasignated = false;
  adultSelected: IAdultSelected = null;
  passengerSelected: Passenger;
  ticketsReissuedByAirline: string[] = [];
  passengersWithAditionalService: string[] = [];
  iataNumber: string;
  fligthNumber = [];

  constructor(
    private itService: ItineraryService,
    private infantControlService: InfantControlService,
    private settingsService: SettingsService
  ) {}

  ngOnInit() {
    this.callComponentSubcriptions();
    this.reset();
  }

  callComponentSubcriptions(): void {
    this.subscriptions.add(
      this.itService
        .getPnrInfo()
        .pipe(take(1))
        .subscribe((pnr) => {
          if (pnr && pnr.ticketsReissuedByAirline) {
            this.iataNumber = pnr.iataNumber;
            this.ticketsReissuedByAirline = pnr.ticketsReissuedByAirline;

            this.fligthNumber = pnr.passengers;
          }

          this.isAirline$ = this.settingsService.getClientIsAirline();
        })
    );
  }

  reset() {
    this.passengersCheck = [];
    this.allPassengersCheck = false;
    (this.passengers || []).forEach((passenger) => (passenger.isSelected = false));
    this.selectPassenger.emit(null);
  }

  detectPassengerWithAditionalService(passengers: Passenger[], aServPax: IAdditionalServicesPax[]) {
    passengers.forEach((passenger) => {
      let hasAdditionalService: boolean;

      aServPax?.forEach((aServ) => {
        aServ?.paxid === passenger?.id && aServ?.additionalServicesList.length > 0
          ? (hasAdditionalService = true)
          : null;
      });

      hasAdditionalService ? this.passengersWithAditionalService.push(passenger.id) : null;
    });
  }

  modalInfantReasignation(passenger: Passenger) {
    this.passengerSelected = passenger;
    this.showInfantReasignationModal = !this.showInfantReasignationModal;
  }

  modalInfantReasignationAccepted(adultSelected: IAdultSelected) {
    this.infantControlService.dispatchSetInfantReassignmentControl({
      adultsInCharge: { fromAdult: adultSelected.fromAdultID, toAdult: adultSelected.toAdultID },
      infantID: adultSelected.infantID
    });

    this.adultSelected = adultSelected;
    this.isInfantReasignated = true;
    this.showInfantReasignationModal = !this.showInfantReasignationModal;
  }

  clearReassignment() {
    this.infantControlService.clearInfantReassignmentControl(
      { fromAdult: this.adultSelected.fromAdultID, toAdult: this.adultSelected.toAdultID },
      this.adultSelected.infantID
    );

    this.isInfantReasignated = false;
    this.adultSelected = null;
  }

  modalSpecialRequests(passenger: Passenger) {
    this.passengerSelected = passenger;
    this.showSpecialRequests = !this.showSpecialRequests;
  }

  selectAll(value: boolean) {
    this.allPassengersCheck = value;
    this.passengersCheck = [];
    (this.passengers || []).forEach((passenger) => {
      passenger.isSelected = value;
      if (this.allPassengersCheck && !!passenger.ticket) {
        this.passengersCheck.push(passenger);
      }
    });
    this.sendSelectedPassengers();
  }

  check(i) {
    if (this.passengers[i].isSelected) {
      this.passengers[i].isSelected = false;
      this.removePassenger(this.passengers[i]);
      this.allPassengersCheck = false;
    } else {
      this.passengers[i].isSelected = true;
      this.passengersCheck.push(this.passengers[i]);
      if (this.areAllChecked()) {
        this.allPassengersCheck = true;
      }
    }
    this.sendSelectedPassengers();
  }

  removePassenger(passengerTarget: Passenger) {
    this.passengersCheck = this.passengersCheck.filter(
      (passenger) => passenger !== passengerTarget
    );
  }

  areAllChecked() {
    return this.passengers.length === this.passengersCheck.length;
  }

  buildPassengersQuote() {
    const passengersQuote = [];
    this.passengersCheck.forEach((passenger) => {
      const passengerQuote: PassengerQuote = {
        firstName: passenger.firstName,
        lastName: passenger.lastName,
        passengerType: passenger.type,
        ticketNumber: passenger.ticket?.number,
        id: passenger.id,
        adultInCharge: passenger?.adultInCharge
      };
      passengersQuote.push(passengerQuote);
    });
    return passengersQuote;
  }

  sendSelectedPassengers() {
    const passengersQuote = this.buildPassengersQuote();
    this.selectPassenger.emit(passengersQuote);
  }

  expand(event, i) {
    event.preventDefault();
    this.passengers[i].isOpen = !this.passengers[i].isOpen;
  }

  getBirthDate(passenger: Passenger): string {
    return passenger.birthDate && new Date(passenger.birthDate).getFullYear() === 1
      ? ''
      : passenger.birthDate;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
